'use client';

import sdk from '@/client/api/sdk';
import { NeonButton } from '@/components/buttons';
import HourSlider from '@/components/client/hour-slider';
import { useDebounce } from '@/hooks/use-debounce';
import { getImageUrl } from '@/lib/img';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import TimelapseIcon from '@mui/icons-material/Timelapse';
import {
  FormControl,
  FormLabel,
  Grid2,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Tooltip,
} from '@mui/material';
import { useTranslate } from '@tolgee/react';
import { useLocale } from 'next-intl';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

// 接口数据类型定义
interface RegionPowerAmount {
  region: string;
  powerAmount: number;
  currencyType: string;
}

interface CalculatorData {
  gpuFamily: string[];
  regionPowerAmount: RegionPowerAmount[];
}

interface CalculatorFormData {
  gpuFamily: string;
  region: string;
  powerAmount: number;
  diskSize: number;
  diskSpeed: number;
  cpuPhysicalCore: number;
  ramCount: number;
  uptime: number;
}

interface IncomeResult {
  daily: number;
  monthly: number;
  yearly: number;
  currency: string;
}

const keys = {
  gpuSelectorLabel: 'provider_page.calculator.gpu_selector.label',
  countrySelectorLabel: 'provider_page.calculator.country_selector.label',
  electricityCostLabel: 'provider_page.calculator.electricity_cost.label',
  diskSizeLabel: 'provider_page.calculator.disk_size.label',
  diskSpeedLabel: 'provider_page.calculator.disk_speed.label',
  cpuPhysicalCoreLabel: 'provider_page.calculator.cpu_physical_core.label',
  ramCountLabel: 'provider_page.calculator.ram_count.label',
  diskSizeUnit: 'provider_page.calculator.disk_size.unit',
  diskSpeedUnit: 'provider_page.calculator.disk_speed.unit',
  cpuPhysicalCoreUnit: 'provider_page.calculator.cpu_physical_core.unit',
  ramCountUnit: 'provider_page.calculator.ram_count.unit',
};

/** 与上方 GPU / 地区 / 电费输入框一致的样式，供硬件参数行复用 */
const calculatorOutlinedFieldSx = {
  backgroundColor: 'rgba(26, 28, 35, 0.6)',
  borderRadius: '12px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.1)',
    },
    '@media (hover: hover)': {
      '&:hover fieldset': {
        borderColor: 'rgba(255, 140, 25, 0.3)',
      },
    },
    '&.Mui-focused fieldset': {
      borderColor: 'rgba(255, 140, 25, 0.6)',
    },
  },
};

/** 磁盘大小（GB）接口与表单允许范围 */
const DISK_SIZE_MIN = 300;
const DISK_SIZE_MAX = 1000;

/** 将磁盘大小限制在合法区间（失焦或提交时用） */
function clampDiskSize(n: number) {
  return Math.min(DISK_SIZE_MAX, Math.max(DISK_SIZE_MIN, n));
}

export default function CalculatorForm({
  calculatorData,
  downloadUrl,
}: {
  calculatorData: CalculatorData | null;
  downloadUrl?: string;
}) {
  const { t } = useTranslate();
  const [incomeResult, setIncomeResult] = useState<IncomeResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const locale = useLocale();

  const { control, watch, setValue, handleSubmit } = useForm<CalculatorFormData>({
    defaultValues: {
      gpuFamily: '5090',
      region: locale === 'zh' ? 'CN' : 'US',
      powerAmount: 0.6,
      diskSize: 1000,
      diskSpeed: 12,
      cpuPhysicalCore: 16,
      ramCount: 64,
      uptime: 24,
    },
  });

  const [gpuFamily, region, powerAmount, diskSize, diskSpeed, cpuPhysicalCore, ramCount, uptime] = watch([
    'gpuFamily',
    'region',
    'powerAmount',
    'diskSize',
    'diskSpeed',
    'cpuPhysicalCore',
    'ramCount',
    'uptime',
  ]);
  const debouncedPowerAmount = useDebounce(powerAmount, 500);
  const debouncedDiskSize = useDebounce(diskSize, 400);
  const debouncedDiskSpeed = useDebounce(diskSpeed, 400);
  const debouncedCpuPhysicalCore = useDebounce(cpuPhysicalCore, 400);
  const debouncedRamCount = useDebounce(ramCount, 400);

  // 当地区变化时，自动设置对应的默认电费
  useEffect(() => {
    if (calculatorData && region) {
      const regionData = calculatorData.regionPowerAmount.find((item) => item.region === region);
      if (regionData) {
        setValue('powerAmount', regionData.powerAmount);
      }
    }
  }, [region, calculatorData, setValue]);

  // 计算收入（防抖处理）
  useEffect(() => {
    if (
      gpuFamily &&
      region &&
      debouncedPowerAmount &&
      uptime &&
      typeof debouncedDiskSize === 'number' &&
      debouncedDiskSize >= DISK_SIZE_MIN &&
      debouncedDiskSize <= DISK_SIZE_MAX &&
      debouncedDiskSpeed &&
      debouncedCpuPhysicalCore &&
      debouncedRamCount
    ) {
      calculateIncome();
    }
  }, [
    gpuFamily,
    region,
    debouncedPowerAmount,
    debouncedDiskSize,
    debouncedDiskSpeed,
    debouncedCpuPhysicalCore,
    debouncedRamCount,
    uptime,
  ]);

  const calculateIncome = async () => {
    if (
      !gpuFamily ||
      !region ||
      !debouncedPowerAmount ||
      !uptime ||
      typeof debouncedDiskSize !== 'number' ||
      debouncedDiskSize < DISK_SIZE_MIN ||
      debouncedDiskSize > DISK_SIZE_MAX ||
      !debouncedDiskSpeed ||
      !debouncedCpuPhysicalCore ||
      !debouncedRamCount
    ) {
      return;
    }

    setIsLoading(true);
    try {
      const resp = await sdk.GET('/api/client/earning/calculator', {
        params: {
          query: {
            gpuFamily,
            region: region as 'CN' | 'US' | 'EU' | 'JP',
            powerAmount: debouncedPowerAmount,
            diskSize: debouncedDiskSize,
            diskSpeed: debouncedDiskSpeed,
            cpuPhysicalCore: debouncedCpuPhysicalCore,
            ramCount: debouncedRamCount,
            onlineTime: uptime,
          },
        },
      });

      if (resp.data && 'data' in resp.data) {
        const responseData = resp.data as any;
        if (responseData.data) {
          setIncomeResult({
            daily: responseData.data.incomeDaily,
            monthly: responseData.data.incomeMonth,
            yearly: responseData.data.incomeYear,
            currency: responseData.data.currencyType,
          });
        }
      }
    } catch (error) {
      console.error('计算收入失败:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // 获取当前货币单位
  const currentCurrency = useMemo(() => {
    if (calculatorData && region) {
      const regionData = calculatorData.regionPowerAmount.find((item) => item.region === region);
      return regionData?.currencyType || 'RMB';
    }
    return 'RMB';
  }, [calculatorData, region]);

  // 格式化金额显示
  const formatAmount = (amount: number) => {
    if (!incomeResult) return '0';
    return `${amount.toFixed(2)} ${incomeResult.currency}`;
  };

  return (
    <form onSubmit={handleSubmit(calculateIncome)} className="space-y-0">
      <div className="mb-6">
        <Grid2 container spacing={3}>
          <Grid2 size={{ xs: 12, md: 'grow' }}>
            <Controller
              name="gpuFamily"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  variant="outlined"
                  className="rounded-lg backdrop-blur-sm"
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(26, 28, 35, 0.6)',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.1)',
                      },
                      '@media (hover: hover)': {
                        '&:hover fieldset': {
                          borderColor: 'rgba(255, 140, 25, 0.3)',
                        },
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'rgba(255, 140, 25, 0.6)',
                      },
                    },
                  }}
                >
                  <InputLabel
                    id={keys.gpuSelectorLabel}
                    sx={{
                      color: 'rgba(180, 180, 190, 0.9)',
                      fontWeight: 500,
                    }}
                  >
                    {t(keys.gpuSelectorLabel)}
                  </InputLabel>
                  <Select
                    {...field}
                    labelId={keys.gpuSelectorLabel}
                    label={t(keys.gpuSelectorLabel)}
                    variant="outlined"
                    sx={{
                      color: '#ffffff',
                      fontWeight: 500,
                      '& .MuiSelect-icon': {
                        color: 'rgba(255, 140, 25, 0.8)',
                      },
                    }}
                    MenuProps={{
                      disableScrollLock: true,
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                          backgroundColor: 'rgba(26, 28, 35, 0.95)',
                          borderRadius: '12px',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                        },
                      },
                    }}
                  >
                    {calculatorData?.gpuFamily.map((gpu) => (
                      <MenuItem
                        key={gpu}
                        value={gpu}
                        sx={{
                          color: '#ffffff',
                          '@media (hover: hover)': {
                            '&:hover': {
                              backgroundColor: 'rgba(255, 140, 25, 0.1)',
                            },
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(255, 140, 25, 0.2)',
                            '@media (hover: hover)': {
                              '&:hover': {
                                backgroundColor: 'rgba(255, 140, 25, 0.3)',
                              },
                            },
                          },
                        }}
                      >
                        {gpu}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 3 }}>
            <Controller
              name="region"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  variant="outlined"
                  className="rounded-lg backdrop-blur-sm"
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(26, 28, 35, 0.6)',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.1)',
                      },
                      '@media (hover: hover)': {
                        '&:hover fieldset': {
                          borderColor: 'rgba(255, 140, 25, 0.3)',
                        },
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'rgba(255, 140, 25, 0.6)',
                      },
                    },
                  }}
                >
                  <InputLabel
                    id={keys.countrySelectorLabel}
                    sx={{
                      color: 'rgba(180, 180, 190, 0.9)',
                      fontWeight: 500,
                    }}
                  >
                    {t(keys.countrySelectorLabel)}
                  </InputLabel>
                  <Select
                    {...field}
                    labelId={keys.countrySelectorLabel}
                    label={t(keys.countrySelectorLabel)}
                    variant="outlined"
                    sx={{
                      color: '#ffffff',
                      fontWeight: 500,
                      '& .MuiSelect-icon': {
                        color: 'rgba(255, 140, 25, 0.8)',
                      },
                    }}
                    MenuProps={{
                      disableScrollLock: true,
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                          backgroundColor: 'rgba(26, 28, 35, 0.95)',
                          borderRadius: '12px',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                        },
                      },
                    }}
                  >
                    {calculatorData?.regionPowerAmount.map((region) => (
                      <MenuItem
                        key={region.region}
                        value={region.region}
                        sx={{
                          color: '#ffffff',
                          '@media (hover: hover)': {
                            '&:hover': {
                              backgroundColor: 'rgba(255, 140, 25, 0.1)',
                            },
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(255, 140, 25, 0.2)',
                            '@media (hover: hover)': {
                              '&:hover': {
                                backgroundColor: 'rgba(255, 140, 25, 0.3)',
                              },
                            },
                          },
                        }}
                      >
                        {region.region === 'CN'
                          ? 'China'
                          : region.region === 'US'
                            ? 'United States'
                            : region.region === 'EU'
                              ? 'Europe'
                              : region.region === 'JP'
                                ? 'Japan'
                                : region.region}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 3 }}>
            <Controller
              name="powerAmount"
              control={control}
              rules={{ required: true, min: 0 }}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  variant="outlined"
                  className="rounded-lg backdrop-blur-sm"
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(26, 28, 35, 0.6)',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.1)',
                      },
                      '@media (hover: hover)': {
                        '&:hover fieldset': {
                          borderColor: 'rgba(255, 140, 25, 0.3)',
                        },
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'rgba(255, 140, 25, 0.6)',
                      },
                    },
                  }}
                >
                  <InputLabel
                    htmlFor={keys.electricityCostLabel}
                    sx={{
                      color: 'rgba(180, 180, 190, 0.9)',
                      fontWeight: 500,
                    }}
                  >
                    {t(keys.electricityCostLabel)}
                  </InputLabel>
                  <OutlinedInput
                    {...field}
                    id={keys.electricityCostLabel}
                    label={t(keys.electricityCostLabel)}
                    inputProps={{
                      type: 'number',
                      step: '0.01',
                    }}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || value === '-') {
                        field.onChange('');
                      } else {
                        const parsedValue = parseFloat(value);
                        if (!isNaN(parsedValue)) {
                          field.onChange(parsedValue);
                        }
                      }
                    }}
                    sx={{
                      color: '#ffffff',
                      fontWeight: 500,
                      caretColor: '#FF8C19',
                    }}
                    endAdornment={
                      <InputAdornment position="end" sx={{ color: 'rgba(180, 180, 190, 0.9)' }}>
                        {currentCurrency}/kWh
                      </InputAdornment>
                    }
                  />
                </FormControl>
              )}
            />
          </Grid2>
        </Grid2>
        <Grid2 container spacing={3} className="mt-3">
          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <Controller
              name="diskSize"
              control={control}
              rules={{ required: true, min: DISK_SIZE_MIN, max: DISK_SIZE_MAX }}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  variant="outlined"
                  className="rounded-lg backdrop-blur-sm"
                  size="small"
                  sx={calculatorOutlinedFieldSx}
                >
                  <InputLabel
                    htmlFor={keys.diskSizeLabel}
                    sx={{
                      color: 'rgba(180, 180, 190, 0.9)',
                      fontWeight: 500,
                    }}
                  >
                    {t(keys.diskSizeLabel)}
                  </InputLabel>
                  <OutlinedInput
                    {...field}
                    id={keys.diskSizeLabel}
                    label={t(keys.diskSizeLabel)}
                    inputProps={{
                      type: 'number',
                      step: '1',
                      min: DISK_SIZE_MIN,
                      max: DISK_SIZE_MAX,
                    }}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || value === '-') {
                        field.onChange('');
                        return;
                      }
                      const parsedValue = parseInt(value, 10);
                      if (Number.isNaN(parsedValue)) {
                        return;
                      }
                      // 超过上限立即纠正，避免框里长时间显示非法值；低于下限不在输入过程中夹紧，以免无法键入「300」等中间态
                      if (parsedValue > DISK_SIZE_MAX) {
                        field.onChange(DISK_SIZE_MAX);
                        return;
                      }
                      field.onChange(parsedValue);
                    }}
                    onBlur={(e) => {
                      field.onBlur();
                      const raw = e.target.value.trim();
                      if (raw === '' || raw === '-') {
                        field.onChange(DISK_SIZE_MIN);
                        return;
                      }
                      const n = parseInt(raw, 10);
                      if (Number.isNaN(n)) {
                        field.onChange(DISK_SIZE_MIN);
                        return;
                      }
                      field.onChange(clampDiskSize(n));
                    }}
                    sx={{
                      color: '#ffffff',
                      fontWeight: 500,
                      caretColor: '#FF8C19',
                    }}
                    endAdornment={
                      <InputAdornment position="end" sx={{ color: 'rgba(180, 180, 190, 0.9)' }}>
                        {t(keys.diskSizeUnit)}
                      </InputAdornment>
                    }
                  />
                </FormControl>
              )}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <Controller
              name="diskSpeed"
              control={control}
              rules={{ required: true, min: 0.01 }}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  variant="outlined"
                  className="rounded-lg backdrop-blur-sm"
                  size="small"
                  sx={calculatorOutlinedFieldSx}
                >
                  <InputLabel
                    htmlFor={keys.diskSpeedLabel}
                    sx={{
                      color: 'rgba(180, 180, 190, 0.9)',
                      fontWeight: 500,
                    }}
                  >
                    {t(keys.diskSpeedLabel)}
                  </InputLabel>
                  <OutlinedInput
                    {...field}
                    id={keys.diskSpeedLabel}
                    label={t(keys.diskSpeedLabel)}
                    inputProps={{
                      type: 'number',
                      step: '0.1',
                      min: '0.01',
                    }}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || value === '-') {
                        field.onChange('');
                      } else {
                        const parsedValue = parseFloat(value);
                        if (!isNaN(parsedValue)) {
                          field.onChange(parsedValue);
                        }
                      }
                    }}
                    sx={{
                      color: '#ffffff',
                      fontWeight: 500,
                      caretColor: '#FF8C19',
                    }}
                    endAdornment={
                      <InputAdornment position="end" sx={{ color: 'rgba(180, 180, 190, 0.9)' }}>
                        {t(keys.diskSpeedUnit)}
                      </InputAdornment>
                    }
                  />
                </FormControl>
              )}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <Controller
              name="cpuPhysicalCore"
              control={control}
              rules={{ required: true, min: 1 }}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  variant="outlined"
                  className="rounded-lg backdrop-blur-sm"
                  size="small"
                  sx={calculatorOutlinedFieldSx}
                >
                  <InputLabel
                    htmlFor={keys.cpuPhysicalCoreLabel}
                    sx={{
                      color: 'rgba(180, 180, 190, 0.9)',
                      fontWeight: 500,
                    }}
                  >
                    {t(keys.cpuPhysicalCoreLabel)}
                  </InputLabel>
                  <OutlinedInput
                    {...field}
                    id={keys.cpuPhysicalCoreLabel}
                    label={t(keys.cpuPhysicalCoreLabel)}
                    inputProps={{
                      type: 'number',
                      step: '1',
                      min: '1',
                    }}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || value === '-') {
                        field.onChange('');
                      } else {
                        const parsedValue = parseInt(value, 10);
                        if (!isNaN(parsedValue)) {
                          field.onChange(parsedValue);
                        }
                      }
                    }}
                    sx={{
                      color: '#ffffff',
                      fontWeight: 500,
                      caretColor: '#FF8C19',
                    }}
                    endAdornment={
                      <InputAdornment position="end" sx={{ color: 'rgba(180, 180, 190, 0.9)' }}>
                        {t(keys.cpuPhysicalCoreUnit)}
                      </InputAdornment>
                    }
                  />
                </FormControl>
              )}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <Controller
              name="ramCount"
              control={control}
              rules={{ required: true, min: 1 }}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  variant="outlined"
                  className="rounded-lg backdrop-blur-sm"
                  size="small"
                  sx={calculatorOutlinedFieldSx}
                >
                  <InputLabel
                    htmlFor={keys.ramCountLabel}
                    sx={{
                      color: 'rgba(180, 180, 190, 0.9)',
                      fontWeight: 500,
                    }}
                  >
                    {t(keys.ramCountLabel)}
                  </InputLabel>
                  <OutlinedInput
                    {...field}
                    id={keys.ramCountLabel}
                    label={t(keys.ramCountLabel)}
                    inputProps={{
                      type: 'number',
                      step: '1',
                      min: '1',
                    }}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || value === '-') {
                        field.onChange('');
                      } else {
                        const parsedValue = parseInt(value, 10);
                        if (!isNaN(parsedValue)) {
                          field.onChange(parsedValue);
                        }
                      }
                    }}
                    sx={{
                      color: '#ffffff',
                      fontWeight: 500,
                      caretColor: '#FF8C19',
                    }}
                    endAdornment={
                      <InputAdornment position="end" sx={{ color: 'rgba(180, 180, 190, 0.9)' }}>
                        {t(keys.ramCountUnit)}
                      </InputAdornment>
                    }
                  />
                </FormControl>
              )}
            />
          </Grid2>
        </Grid2>
      </div>

      <FormControl fullWidth className="mb-6 space-y-6">
        <FormLabel
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: '#ffffff',
            fontWeight: 500,
          }}
        >
          <TimelapseIcon fontSize="small" sx={{ color: '#FF8C19' }} />
          <span style={{ fontSize: '1rem' }}>{t('provider_page.calculator.uptime_slider.label')}</span>{' '}
          <Tooltip
            title={t('provider_page.calculator.uptime_slider.note')}
            placement="right"
            arrow
            className="cursor-help"
            sx={{
              tooltip: {
                backgroundColor: 'rgba(26, 28, 35, 0.95)',
                border: '1px solid rgba(255, 140, 25, 0.3)',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
              },
            }}
          >
            <HelpOutlineIcon
              fontSize="small"
              sx={{
                color: 'rgba(180, 180, 190, 0.9)',
                transition: 'color 0.2s',
                '@media (hover: hover)': {
                  '&:hover': {
                    color: '#ffffff',
                  },
                },
              }}
            />
          </Tooltip>
        </FormLabel>
        <div className="px-4">
          <Controller
            name="uptime"
            control={control}
            rules={{ required: true, min: 1, max: 24 }}
            render={({ field }) => <HourSlider value={field.value} onChange={(value) => field.onChange(value)} />}
          />
        </div>
      </FormControl>

      <div className="border-t border-white/10 pt-6" style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}>
        <div className="flex flex-wrap gap-6 lg:flex-nowrap">
          <div className="flex-1 space-y-4">
            <div
              className="transform rounded-xl p-4 backdrop-blur-sm transition-transform [@media(hover:hover)]:hover:scale-[1.02] [@media(hover:hover)]:hover:shadow-lg"
              style={{
                backgroundColor: 'rgba(26, 28, 35, 0.7)',
                borderRadius: '16px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                border: '1px solid rgba(255, 255, 255, 0.05)',
                transition: 'all 0.3s ease',
                minHeight: '80px',
                overflow: 'visible',
              }}
            >
              <div className="mb-2 flex items-center text-base font-bold" style={{ color: '#ffffff' }}>
                <span
                  style={{
                    marginRight: '8px',
                    height: '24px',
                    width: '6px',
                    borderRadius: '4px',
                    backgroundColor: '#FF8C19',
                    boxShadow: '0 0 10px rgba(255, 140, 25, 0.5)',
                  }}
                ></span>
                {t('provider_page.calculator.earning.results.day')}
              </div>
              <div
                style={{
                  backgroundImage: 'linear-gradient(to right, #FF8C19, #F59E0B)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontSize: '1.875rem',
                  fontWeight: 700,
                  lineHeight: '1.2',
                  paddingBottom: '4px',
                  color: '#FF8C19',
                  overflow: 'visible',
                  display: 'block',
                }}
              >
                {incomeResult ? formatAmount(incomeResult.daily) : '0'}
              </div>
            </div>

            <div
              className="transform rounded-xl p-4 backdrop-blur-sm transition-transform [@media(hover:hover)]:hover:scale-[1.02] [@media(hover:hover)]:hover:shadow-lg"
              style={{
                backgroundColor: 'rgba(26, 28, 35, 0.7)',
                borderRadius: '16px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                border: '1px solid rgba(255, 255, 255, 0.05)',
                transition: 'all 0.3s ease',
                minHeight: '80px',
                overflow: 'visible',
              }}
            >
              <div className="mb-2 flex items-center text-base font-bold" style={{ color: '#ffffff' }}>
                <span
                  style={{
                    marginRight: '8px',
                    height: '24px',
                    width: '6px',
                    borderRadius: '4px',
                    backgroundColor: '#FF8C19',
                    boxShadow: '0 0 10px rgba(255, 140, 25, 0.5)',
                  }}
                ></span>
                {t('provider_page.calculator.earning.results.month')}
              </div>
              <div
                style={{
                  backgroundImage: 'linear-gradient(to right, #FF8C19, #F59E0B)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontSize: '1.875rem',
                  fontWeight: 700,
                  lineHeight: '1.2',
                  paddingBottom: '4px',
                  color: '#FF8C19',
                  overflow: 'visible',
                  display: 'block',
                }}
              >
                {incomeResult ? formatAmount(incomeResult.monthly) : '0'}
              </div>
            </div>

            <div
              className="transform rounded-xl p-4 backdrop-blur-sm transition-transform [@media(hover:hover)]:hover:scale-[1.02] [@media(hover:hover)]:hover:shadow-lg"
              style={{
                backgroundColor: 'rgba(26, 28, 35, 0.7)',
                borderRadius: '16px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                border: '1px solid rgba(255, 255, 255, 0.05)',
                transition: 'all 0.3s ease',
                minHeight: '80px',
                overflow: 'visible',
              }}
            >
              <div className="mb-2 flex items-center text-base font-bold" style={{ color: '#ffffff' }}>
                <span
                  style={{
                    marginRight: '8px',
                    height: '24px',
                    width: '6px',
                    borderRadius: '4px',
                    backgroundColor: '#6F43F3',
                    boxShadow: '0 0 10px rgba(111, 67, 243, 0.5)',
                  }}
                ></span>
                {t('provider_page.calculator.earning.results.year')}
              </div>
              <div
                style={{
                  backgroundImage: 'linear-gradient(to right, #6F43F3, #A855F7)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontSize: '1.875rem',
                  fontWeight: 700,
                  lineHeight: '1.2',
                  paddingBottom: '4px',
                  color: '#6F43F3',
                  overflow: 'visible',
                  display: 'block',
                }}
              >
                {incomeResult ? formatAmount(incomeResult.yearly) : '0'}
              </div>
            </div>

            <div className="flex justify-center pt-2 lg:justify-start lg:ml-4">
              <Tooltip title={t('computing.power.service.not_online.announcement_channel')} placement="top">
                <NeonButton className="transform transition-transform [@media(hover:hover)]:hover:scale-105">
                  {t('provider_page.download_text')}
                </NeonButton>
              </Tooltip>
            </div>
          </div>

          <div className="hidden flex-1 items-center justify-center lg:flex">
            <div className="relative">
              <div
                className="absolute inset-0 rounded-[25%] blur-md"
                style={{
                  backgroundImage: 'linear-gradient(to top right, rgba(255, 140, 25, 0.15), rgba(111, 67, 243, 0.15))',
                }}
              ></div>
              <div
                className="relative overflow-hidden rounded-[25%] border-4 border-white/10 shadow-2xl"
                style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}
              >
                <img
                  width={320}
                  height={320}
                  className="duration-5000 brightness-100 transition-transform ease-in-out [@media(hover:hover)]:hover:scale-110"
                  src={getImageUrl('/home.page/3.png', '400webp')}
                  alt="Earnings visualization"
                />
                <div
                  className="absolute inset-0 mix-blend-overlay"
                  style={{
                    backgroundImage:
                      'linear-gradient(to top right, rgba(255, 140, 25, 0.15), rgba(111, 67, 243, 0.15))',
                  }}
                ></div>
              </div>
              <div
                className="absolute -right-4 -top-4 h-16 w-16 animate-pulse rounded-full blur-xl"
                style={{
                  backgroundColor: 'rgba(255, 140, 25, 0.25)',
                  animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                }}
              ></div>
              <div
                className="absolute -bottom-4 -left-4 h-16 w-16 animate-pulse rounded-full blur-xl"
                style={{
                  backgroundColor: 'rgba(111, 67, 243, 0.25)',
                  animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite 0.5s',
                }}
              ></div>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
}
